import * as React from "react";
import { Link, useMediaQuery } from "@mui/material";
import Grid from '@mui/material/Grid2';
import _ from "lodash";
import { useSnackbar } from "notistack";
import { useNavigate, useLocation } from "react-router-dom";
// import components needed for this
import GenericTabs from "../../components/GenericTabs/GenericTabs";
import AffiliateRecordAttendeeTable from "./AffiliateRecordAttendeeTable";
import ComplianceScorecard, { ISelectedAffiliate as ComplianceScorecardSelectedAffiliate } from "./ComplianceScorecard";
import GenericSpinner from "../../components/GenericSpinner/GenericSpinner";
// import services
import { getAffiliateById } from "../../actions/AffiliateActions/AffiliateActions";
// Zustand store
import { useAppStore } from "../../stores";
import AffiliateRecordInfo from "./AffiliateRecordInfo";
// permissions
import { usertype_permissions } from "../../enums/PermissionEnums";
interface ISelectedAffiliate {
  delegates?: unknown[];
  alternates?: unknown[];
  otherAttendees?: unknown[];
  affiliateName?: string;
  affiliateCompliance?: unknown;
  president?: string;
  local?: number;
  localNumber?: string;
  delegateEntitlement?: number;
  secretary?: string;
  delegateCount?: number;
  treasurer?: string;
  alternateCount?: number;
  totalVotes?: number;
  [key: string]: unknown;
}

interface IAffiliateSingleRecordDisplayProps {
  onRowClick?: (data: unknown) => void;
}
const AffiliateSingleRecordDisplay: React.FunctionComponent<
  IAffiliateSingleRecordDisplayProps
> = (props) => {
  const displayMobile = useMediaQuery("(max-width:959px)");
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const affiliateGuid: string = location.pathname.split("/")[3];

  // Zustand store
  const selectedAffiliate = useAppStore((state) => state.affiliate?.selectedAffiliate) as ISelectedAffiliate | undefined;
  const selectedAffiliateLoaded = useAppStore((state) => state.affiliate?.selectedAffiliateRequestSuccess || false);
  const permissions = useAppStore((state) => state.user?.permissions || []);
  const setSelectedAffiliate = useAppStore((state) => state.setSelectedAffiliate);
  const setSelectedRequestSuccess = useAppStore((state) => state.setSelectedRequestSuccess);
  const setSelectedRequestLoading = useAppStore((state) => state.setSelectedRequestLoading);
  const isAdmin =
    _.find(permissions, (permission: any) => {
      return (
        permission.name.indexOf(usertype_permissions.Admin) !== -1 ||
        permission.name.indexOf(usertype_permissions.BackOffice) !== -1
      );
    }) !== undefined;
  React.useEffect(() => {
    setSelectedRequestLoading();
    getAffiliateById(affiliateGuid)
      .then((response: any) => {
        if (response && response.data) {
          setSelectedAffiliate(response.data);
          setSelectedRequestSuccess();
        }
      })
      .catch((error) => {
        enqueueSnackbar("error: " + error, { variant: "error" });
        navigate(-1);
      });
  }, []);
  const spinnerForLoading = (
    <GenericSpinner includeOverlay={true} padding={25} />
  );
  const complianceScorecard = (
    <Grid size={{ xs: 12 }}>
      <ComplianceScorecard selectedAffiliate={selectedAffiliate as ComplianceScorecardSelectedAffiliate} />
    </Grid>
  );
  const attendeeTabs = (
    <Grid size={{ xs: 12 }} className={"pt-2"}>
      <GenericTabs
        tabs={[
          { name: "Delegates" },
          { name: "Alternates" },
          { name: "Other" },
        ]}
        tabContent={[
          {
            content: (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <AffiliateRecordAttendeeTable
                    title={"Delegates"}
                    records={selectedAffiliate && selectedAffiliate.delegates}
                    onRowClick={props.onRowClick}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <p>
                    *Contact compliance@aft.org for questions and information
                    regarding compliance.
                  </p>
                </Grid>
              </Grid>
            ),
          },
          {
            content: (
              <AffiliateRecordAttendeeTable
                title={"Alternates"}
                records={selectedAffiliate && selectedAffiliate.alternates}
                onRowClick={props.onRowClick}
              />
            ),
          },
          {
            content: (
              <AffiliateRecordAttendeeTable
                title={"Other"}
                records={selectedAffiliate && selectedAffiliate.otherAttendees}
                onRowClick={props.onRowClick}
              />
            ),
          },
        ]}
      />
    </Grid>
  );
  return (
    <Grid container size={{ xs: 12 }}>
      {!selectedAffiliateLoaded ? (
        spinnerForLoading
      ) : (
        <Grid container size={{ xs: 12 }}>
          {isAdmin && (
            <Grid size={{ xs: 12 }} className="pt-3 pb-3">
              <Link className={"back-btn"} onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>
                {"< "}Back to Search Results
              </Link>
            </Grid>
          )}
          <Grid
            size={{ xs: 12, lg: 8 }}
            className={"pt-3 main-affiliate-information"}
          >
            {selectedAffiliate && (
              <Grid container size={{ xs: 12 }}>
                <Grid size={{ xs: 12 }}>
                  <span className="affiliate-name">
                    {selectedAffiliate && selectedAffiliate.affiliateName
                      ? selectedAffiliate.affiliateName
                      : "N/A"}
                  </span>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <AffiliateRecordInfo
                    selectedAffiliate={selectedAffiliate && selectedAffiliate}
                  />
                </Grid>
                {!displayMobile && (
                  <Grid size={{ xs: 12 }} className={"pt-3"}>
                    <div>
                      <span className={"who-is-attending-label"}>
                        Who is Attending:
                      </span>
                    </div>
                    {attendeeTabs}
                  </Grid>
                )}
              </Grid>
            )}
          </Grid>
          {!displayMobile ? (
            <Grid
              size={{ md: 12, lg: 4 }}
              style={{ paddingLeft: !displayMobile ? "2rem" : "0" }}
            >
              {selectedAffiliate && (
                <Grid container size={{ xs: 12 }}>
                  <Grid size={{ xs: 12 }}>
                    {complianceScorecard}
                  </Grid>
                </Grid>
              )}
            </Grid>
          ) : (
            <Grid size={{ xs: 12 }} className={"pt-5"}>
              <GenericTabs
                className={"mobile-tabs"}
                tabs={[
                  {
                    name: "Scorecard",
                    className: "mobile-tab",
                  },
                  {
                    name: "Attending",
                    className: "mobile-tab",
                  },
                ]}
                tabContent={[
                  {
                    content: complianceScorecard,
                  },
                  {
                    content: attendeeTabs,
                  },
                ]}
              />
            </Grid>
          )}
        </Grid>
      )}
    </Grid>
  );
};
export default AffiliateSingleRecordDisplay;